import { Application, Container, Graphics } from "pixi.js";
import {
  DEMO_FORM,
  KEY_BY_COUNT,
  type CodeKeyStimulus,
  minimumTaps,
  packToken,
} from "@talent/tasks";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime } from "./sound.js";
import { COLOR, familyColor, lighten, mix } from "./paint.js";
import { TOKEN_COLORS, drawToken } from "./token.js";

/**
 * Colour by the key — the legend at the top, the board below.
 *
 * A tile starts grey. Tapping it gives it the first colour, tapping again the second, and after the
 * last it comes back round. The trial ends when every tile has a colour and the child has stopped
 * touching them.
 *
 * **There is no confirm button, and the settle delay is what replaces it.** A board finishes the
 * moment the last tile is coloured, which would mean a child who mis-taps that last tile has lost
 * the trial with no way back — every other tile on the board is correctable and the last one would
 * not be. So the trial resolves a beat after the final tap instead, and any further tap cancels the
 * pending resolution and starts the beat again. A button would be text on a surface that may not
 * contain any (v2 §15), and a five-year-old should not have to find a second control to finish
 * something they can see is finished.
 *
 * **Cycling rather than a palette.** The obvious design is a row of colour buttons and a selected
 * one, which is two things to hold — where you are in the board, and which colour is armed. Tapping
 * the thing itself keeps the child's attention on the thing.
 */

export interface CodeKeyTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface Attempt {
  painted: number[];
  taps: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateStimulus: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => CodeKeyStimulus;
  scoreTrial: (stimulus: CodeKeyStimulus, painted: readonly number[]) => boolean;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  startAt?: number;
}

/** A tile that has not been given a colour yet. Grey, so "undecided" is visible at a glance. */
const BLANK = -1;

/**
 * How long the board waits after the last tap before it counts itself finished.
 *
 * Long enough to change your mind, short enough not to feel broken. Every tap restarts it, so the
 * only way to reach the end of it is to have stopped.
 */
const SETTLE_MS = 1200;

export async function runCodeKeyTask(opts: RunOptions): Promise<CodeKeyTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h * 0.16, Math.min(w, h) * 0.44).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  /**
   * One "thing" — a shape, or a group of dots — at `size`, in `colour`.
   *
   * The two kinds of key share this so the legend and the board cannot disagree about what a thing
   * looks like. A count is drawn as dots in a fixed arrangement rather than a numeral, because this
   * game may not put script on the canvas (v2 §15) and because the level is about the QUANTITY.
   */
  function drawThing(
    g: Graphics,
    kind: number,
    look: number,
    size: number,
    colour: number | null,
  ): void {
    const ink = colour === null ? mix(COLOR.card, COLOR.stage, 0.45) : colour;
    if (kind === KEY_BY_COUNT) {
      const r = size * 0.085;
      // Two rows of two, so three is a triangle and four a square — arrangements a child reads
      // without counting, which is the skill the count levels are actually building on.
      const spread = size * 0.17;
      const spots: [number, number][] =
        look === 1
          ? [[0, 0]]
          : look === 2
            ? [
                [-spread, 0],
                [spread, 0],
              ]
            : look === 3
              ? [
                  [0, -spread],
                  [-spread, spread],
                  [spread, spread],
                ]
              : [
                  [-spread, -spread],
                  [spread, -spread],
                  [-spread, spread],
                  [spread, spread],
                ];
      for (const [dx, dy] of spots) {
        g.circle(dx, dy, r).fill(ink);
        g.circle(dx - r * 0.28, dy - r * 0.3, r * 0.3).fill({ color: 0xffffff, alpha: 0.35 });
      }
      return;
    }
    // The shape vocabulary is the one every other game draws, so a fish is the same fish here.
    // `drawToken` paints a tile too, which is exactly what a board square needs.
    drawToken(g, packToken(look, 0), size, "idle", accent, ink);
  }

  function present(stimulus: CodeKeyStimulus): {
    attempt: Promise<Attempt>;
    simulateTap: (item: number) => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    const legend = new Container();
    const board = new Container();
    layer.addChild(legend, board);

    const painted = stimulus.items.map(() => BLANK);
    let taps = 0;
    let accepting = true;
    let settleToken = 0;
    let resolveAttempt: ((a: Attempt) => void) | null = null;

    let tiles: Graphics[] = [];
    let tileSize = 0;

    function drawTile(index: number): void {
      const g = tiles[index];
      const look = stimulus.items[index];
      if (g === undefined || look === undefined) return;
      const colour = painted[index] ?? BLANK;
      const ink = colour === BLANK ? null : (TOKEN_COLORS[colour] ?? COLOR.sunny);
      g.clear();
      if (stimulus.kind === KEY_BY_COUNT) {
        // The count kind draws no tile of its own, so it needs a panel to sit on — otherwise the
        // dots float on the backdrop and stop reading as a thing to tap.
        const panel =
          ink === null ? mix(COLOR.stage, COLOR.card, 0.12) : mix(ink, COLOR.stage, 0.72);
        g.roundRect(-tileSize / 2, -tileSize / 2, tileSize, tileSize, tileSize * 0.24).fill(panel);
      }
      drawThing(g, stimulus.kind, look, tileSize, ink);
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const count = stimulus.items.length;
      const cols = count <= 4 ? 2 : 3;
      const rows = Math.ceil(count / cols);
      const gap = Math.max(10, w * 0.03);

      const topReserved = 56;
      const bottomReserved = 24;
      const keySize = Math.min((w * 0.9 - gap * (stimulus.key.length - 1)) / stimulus.key.length, 72);
      const keyBlock = keySize + gap * 2.2;

      const usableH = h - topReserved - bottomReserved - keyBlock;
      const byWidth = (w * 0.92 - gap * (cols - 1)) / cols;
      const byHeight = (usableH - gap * (rows - 1)) / rows;
      tileSize = Math.max(48, Math.min(byWidth, byHeight, 132));

      /**
       * The legend, on a bar of its own.
       *
       * Drawn as a strip behind the entries rather than as loose tiles, because it has to read as a
       * REFERENCE rather than as another row of things to colour — and a child who taps it and gets
       * nothing has to be able to see why.
       */
      for (const child of legend.removeChildren()) child.destroy();
      const keyW = keySize * stimulus.key.length + gap * (stimulus.key.length - 1);
      const bar = new Graphics();
      bar
        .roundRect(
          w / 2 - keyW / 2 - gap * 0.7,
          topReserved - gap * 0.5,
          keyW + gap * 1.4,
          keySize + gap,
          keySize * 0.3,
        )
        .fill({ color: 0xffffff, alpha: 0.06 })
        .stroke({ width: 2, color: accent, alpha: 0.4 });
      legend.addChild(bar);

      stimulus.key.forEach((look, colour) => {
        const swatch = TOKEN_COLORS[colour] ?? COLOR.sunny;
        const at = {
          x: w / 2 - keyW / 2 + keySize / 2 + colour * (keySize + gap),
          y: topReserved + keySize / 2,
        };
        /**
         * The coloured backing and the thing on it are two Graphics, not one.
         *
         * `drawThing` reaches `drawToken` for a shape key, and `drawToken` opens with `clear()` —
         * so a panel painted first was wiped, and every entry in the legend came out wearing the
         * same neutral tile. Which colour an entry names is the entire content of a legend, so it
         * gets a layer nothing else is allowed to erase.
         */
        const panel = new Graphics();
        panel
          .roundRect(-keySize / 2, -keySize / 2, keySize, keySize, keySize * 0.26)
          .fill(mix(swatch, COLOR.stage, 0.62));
        panel.position.set(at.x, at.y);
        const art = new Graphics();
        drawThing(art, stimulus.kind, look, keySize, swatch);
        art.position.set(at.x, at.y);
        legend.addChild(panel, art);
      });

      const boardW = tileSize * cols + gap * (cols - 1);
      const boardH = tileSize * rows + gap * (rows - 1);
      const originX = (w - boardW) / 2 + tileSize / 2;
      const originY = topReserved + keyBlock + (usableH - boardH) / 2 + tileSize / 2;

      for (const child of board.removeChildren()) child.destroy();
      tiles = [];
      for (let index = 0; index < count; index++) {
        const g = new Graphics();
        g.eventMode = "static";
        g.cursor = "pointer";
        const row = Math.floor(index / cols);
        // A short last row is centred, so five tiles do not sit lopsided in a 3-wide board.
        const inRow = Math.min(cols, count - row * cols);
        g.position.set(
          originX + (cols - inRow) * ((tileSize + gap) / 2) + (index % cols) * (tileSize + gap),
          originY + row * (tileSize + gap),
        );
        const half = tileSize / 2 + tileSize * 0.08;
        g.hitArea = {
          contains: (hx: number, hy: number) =>
            hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
        g.on("pointertap", () => onTap(index));
        board.addChild(g);
        tiles.push(g);
        drawTile(index);
      }
    }

    /** Schedules the finish, cancelling any finish already pending. */
    function maybeFinish(): void {
      if (painted.some((c) => c === BLANK)) return;
      const token = ++settleToken;
      void idle(SETTLE_MS).then(() => {
        if (token !== settleToken || !accepting) return;
        accepting = false;
        resolveAttempt?.({ painted: [...painted], taps });
      });
    }

    function onTap(index: number): void {
      if (!accepting) return;
      const g = tiles[index];
      if (!g) return;

      const was = painted[index] ?? BLANK;
      const next = was === BLANK ? 0 : (was + 1) % stimulus.key.length;
      painted[index] = next;
      taps++;
      drawTile(index);
      cellTone(next);
      ripple(fx, g.position.x, g.position.y, TOKEN_COLORS[next] ?? accent, tileSize);
      void pop(g, { scale: 1.08 });
      // Cancels any pending finish as a side effect: a tap on an already-coloured tile restarts
      // the beat, which is what makes the last tile as correctable as every other one.
      settleToken++;
      maybeFinish();
    }

    layout();
    app.renderer.on("resize", layout);
    glow(glowLayer, app.renderer.width / 2, 88, 90, accent, 900);

    if (!prefersReducedMotion()) {
      board.children.forEach((child, i) => {
        child.scale.set(0);
        void idle(60 + i * 40).then(() => animate(280, (t) => child.scale.set(easeOutBack(t))));
      });
    }

    const attempt = new Promise<Attempt>((resolve) => {
      resolveAttempt = resolve;
    }).then(async (result) => {
      const correct = opts.scoreTrial(stimulus, result.painted);
      if (correct) {
        successChime();
        for (const g of tiles) {
          burst(fx, g.position.x, g.position.y, [accent, lighten(accent, 0.4), COLOR.correct], 8);
        }
        await idle(520);
      } else {
        missTone();
        // Only the wrong tiles are shaken, then corrected in place. Repainting the whole board
        // would hide which ones the child actually got right, which is the only feedback here.
        const wrong = stimulus.answer.flatMap((c, i) => (result.painted[i] === c ? [] : [i]));
        await Promise.all(wrong.map((i) => (tiles[i] ? nudge(tiles[i], 5) : Promise.resolve())));
        await idle(260);
        for (const i of wrong) {
          painted[i] = stimulus.answer[i] ?? 0;
          drawTile(i);
          const g = tiles[i];
          if (g) glow(glowLayer, g.position.x, g.position.y, tileSize * 0.6, COLOR.correct, 520);
          cellTone(painted[i] ?? 0, { volume: 0.09 });
          await idle(300);
        }
        await idle(520);
      }
      return result;
    });

    return {
      attempt,
      simulateTap: onTap,
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: CodeKeyTaskResult["reason"] = "completed";

  /**
   * A wordless demonstration, before anything is scored.
   *
   * Nothing on this screen announces that the bar at the top is an INSTRUCTION, or that a tile
   * cycles rather than toggles. Both are invisible and neither can be said in words here. So the
   * game colours one tile itself: the matching legend entry lights, the tile takes its colour, and
   * it happens again for a second tile.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateStimulus(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(demo);
      await idle(1000);
      for (let i = 0; i < demo.items.length; i++) {
        if (opts.signal?.aborted) break;
        // Tapped as many times as the answer needs, so the child sees the cycle rather than a
        // colour appearing by magic.
        for (let t = 0; t <= (demo.answer[i] ?? 0); t++) {
          shown.simulateTap(i);
          await idle(340);
        }
        await idle(220);
      }
      if (!opts.signal?.aborted) {
        await untilAbandoned(shown.attempt, opts.signal);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const stimulus = opts.generateStimulus(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The legend first, then the board. Both are needed to re-score the trial later: the same
        // board means different answers under a different key.
        stimulus: [stimulus.kind, ...stimulus.key, ...stimulus.items],
        options: [stimulus.answer],
        correctIndex: stimulus.key.length,
      });

      const start = performance.now();
      const trial = present(stimulus);
      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const correct = opts.scoreTrial(stimulus, outcome.painted);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        response: outcome.painted,
        correct,
        responseMs: elapsed,
        /**
         * Taps spent beyond the fewest that could have done it.
         *
         * The signal this game has that no other one does. A child who reads the legend goes
         * straight to each colour; a child who cycles until something looks right spends three
         * times as many taps and can still finish the board correctly. Both are "correct" and they
         * are not the same performance.
         */
        corrections: Math.max(0, outcome.taps - minimumTaps(stimulus)),
      });

      const fast = detectImplausibleSpeed(elapsed, stimulus.items.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
